/* eslint-disable @typescript-eslint/no-unused-vars */
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Lottie from "lottie-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import ExternalSubscriptionArrowGif from "../../assets/externalsubscription-arrow.gif";
import { useDetectDevice } from "../../hooks/use-detect-device";
import { useQueryParam } from "../../hooks/use-query-string";
import { useProfile } from "../profile/queries";
import SucessAnimation from "../recipe-book/preferences/preferences-saved-animation.json";
import { useSubscription } from "../subscription-external/queries";
import FailedAnimation from "../subscription/error.json";
import "../subscription/styles.scss";
import PaymentModalLoadingSkeleton from "./payment-modal-loading-skeleton";
import PaymentSubscriptionDetail from "./payment-sub-details";
import { useVerifyPayment } from "./queries";
import { mixpanelTrack } from "../../utils/mixpanel-utils";

interface Props {
    onClose: Dispatch<SetStateAction<boolean>>;
    open: boolean;
}

export function PaymentModal({ onClose, open }: Props) {
    const { search } = useLocation();
    const [searchParams] = useState(new URLSearchParams(search));
    const uuid = useQueryParam("uuid", "");
    const tz = useQueryParam("timezone", "Central");
    const deviceDetect = useDetectDevice();

    const { refetch: refetchProfile } = useProfile(true);
    const {
        data: subscription,
        isLoading,
        refetch: refetchSubscription,
    } = useSubscription(uuid, tz);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [failedPayment, setFailedPayment] = useState(false);

    const {
        product_id: productId,
        session_id: sessionId,
        success: paymentSuccessParam,
        cancel: paymentFailedParam,
    } = Object.fromEntries(searchParams.entries());

    const paymentSuccess = paymentSuccessParam === "true";
    const paymentFailed = failedPayment || paymentFailedParam === "true";

    const { mutate: verifyPayment, isLoading: isPaymentVerifying } =
        useVerifyPayment();

    useEffect(() => {
        if (sessionId && (paymentSuccess || paymentFailed)) {
            verifyPayment(
                { sessionId, productId },
                {
                    onSuccess: () => {
                        void refetchSubscription();
                    },
                    onError: () => {
                        setFailedPayment(true);
                    },
                }
            );
        }
    }, [
        sessionId,
        paymentSuccess,
        paymentFailed,
        productId,
        verifyPayment,
        refetchProfile,
        refetchSubscription,
    ]);

    const appLink = () => {
        if (import.meta.env.VITE_APP_MIXPANEL_TOKEN?.includes("develop")) {
            return "https://app-develop.habitnu.com/";
        }
        return "https://app.habitnu.com/";
    };

    if (isPaymentVerifying || isLoading) {
        return (
            <Modal
                onHide={() => {
                    onClose(false);
                }}
                show={open}
                className="payment-modal-lg"
            >
                <Modal.Header
                    closeButton
                    data-attr-id="loading-modal-close-btn"
                >
                    <PaymentModalLoadingSkeleton />
                </Modal.Header>
            </Modal>
        );
    }

    return (
        <Modal
            onHide={() => {
                mixpanelTrack("Close Button Click", {
                    step: paymentFailed ? "Payment Failed" : "Payment Success",
                    flow: "External Subscription",
                });
                onClose(false);
            }}
            show={open}
            className="payment-modal-lg"
        >
            <Modal.Header
                closeButton
                data-attr-id="success-modal-close-btn"
                className={paymentFailed ? "failed-modal-header" : ""}
            />
            {paymentFailed && (
                <Modal.Body className="d-flex failed-payment flex-column align-items-center justify-content-center p-0">
                    <Lottie
                        animationData={FailedAnimation}
                        autoPlay
                        loop={false}
                        className="lottie"
                        style={{ width: "85px", height: "90px" }}
                        aria-label="Payment Failed Animation"
                    />
                    <h5 style={{ fontWeight: "bold" }}>
                        {t("payment_modal.try_again")}
                    </h5>
                    <h6>{t("payment_modal.unable_to_process")}</h6>
                    <div className="payment-failed-info">
                        <span>
                            <FontAwesomeIcon icon={faCircleInfo} />
                        </span>
                        <p>
                            Please check your card or app store settings and try
                            again.
                        </p>
                    </div>
                </Modal.Body>
            )}
            {!paymentFailed && (
                <Modal.Body className="d-flex flex-column align-items-center justify-content-center p-0">
                    <Lottie
                        animationData={SucessAnimation}
                        autoPlay
                        loop={false}
                        className="lottie"
                        style={{ width: "120px", height: "130px" }}
                        aria-label="Payment Success Animation"
                    />
                    <h5 style={{ fontWeight: "bold" }}>
                        {t("payment_modal.you_are_all_set")}
                    </h5>
                    {subscription && (
                        <PaymentSubscriptionDetail
                            subscription={subscription}
                            timezone={sessionStorage.getItem("timezone") || tz}
                        />
                    )}
                    <div className="modal-footer">
                        <a
                            className="mt-2 btn-success d-flex justify-content-center align-items-center gap-1 text-decoration-none"
                            data-attr-id="success-modal-login-btn"
                            href={
                                deviceDetect.isDesktop()
                                    ? appLink()
                                    : "habitnu://withings/callback"
                            }
                            onClick={() => {
                                mixpanelTrack("Go To App Click", {
                                    step: "Login",
                                    flow: "External Subscription",
                                });
                            }}
                        >
                            {t("payment_modal.go_to_app")}
                            <img
                                src={ExternalSubscriptionArrowGif}
                                alt="arrow"
                                className="subscription-arrow-gif"
                            />
                        </a>
                    </div>
                </Modal.Body>
            )}
        </Modal>
    );
}
